import PageInfo from "@/components/PageInfo";
import {
  Container,
  chakra,
  Box,
  Text,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Grid,
  Button,
  useToast,
} from "@chakra-ui/react";
import Navbar from "@/components/Navbar";
import { useClientsideUserData, useUserData, withUserData } from "@/contexts/UserDataContext";
import Footer from "@/components/Footer";
import { IoMdSchool } from "react-icons/io";
import { SiTrustpilot } from "react-icons/si";
import { BiSolidSchool, BiSupport } from "react-icons/bi";
import { TbDeviceAnalytics } from "react-icons/tb";
import { BsLaptop } from "react-icons/bs";
import { StarIcon } from "@chakra-ui/icons";
import { purchaseAnalytics } from "@/utils/analytics";
import { useState } from "react";
import { useRouter } from "next/router";
import { useAPI } from "@/contexts/APIContext";
import { ENTERPRISE_GO, ENTERPRISE_PRO, ENTERPRISE_GOLD } from "@/utils/data/products";
import ModalCheckoutForm from "@/components/plans/ModalCheckoutForm";
import ModalEnterpriseCheckoutForm from "@/components/plans/ModalEnterpriseCheckoutForm";
import Image from "next/image";
import ModalAuth from "@/components/user/ModalAuth";
import ModalEnterpriseError from "@/components/plans/ModalEnterpriseError";

const Partnerships = () => {
  const router = useRouter();
  const api = useAPI();
  const toast = useToast();
  const { userData, currentUser } = useUserData();

  const type = userData.type;

  useClientsideUserData();

  const [loading, setLoading] = useState(false);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [enterpriseErrorVisible, setEnterpriseErrorVisible] = useState(false);

  const initPurchase = async (enterprisePlan) => {
    if (!currentUser) {
      setShowAuthModal(true);

      return;
    }

    if (currentUser && userData.type !== "enterprise") {
      setEnterpriseErrorVisible(true);

      return;
    }

    setLoading(true);

    const response = await api.post("/stripe/payment-intent", true, {
      enterprise_plan: enterprisePlan,
    });

    if (!!response) {
      const { clientSecret } = response;

      const query = { ...router.query, clientSecret };

      if (enterprisePlan && !router.query.enterprise_plan) {
        query.enterprise_plan = enterprisePlan;
      }

      purchaseAnalytics.onCheckoutShowPaymentModal();

      router.push({
        ...router,
        query,
      });
    } else {
      toast({
        title: "Error",
        description: `There was an error processing the request. Server returned: ${response}`,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }

    setLoading(false);
  };

  return (
    <>
      <PageInfo
        title="Partnerships"
        themeColor="beige"
        description="We offer competitive packages for staff to support students on their admission journey."
      />

      <Box
        minH="100vh"
        background="radial-gradient(circle, rgba(53,19,67,1) 0%, rgba(41,19,86,1) 47%, rgba(53,19,67,1) 92%)"
        pb={150}
      >
        <Navbar
          inverted
          customLogo={
            <Flex direction="column" mt={2}>
              <Text fontSize={{ md: "sm", lg: "3xl" }} fontWeight={600} lineHeight="16px">
                admityogi
              </Text>

              <Text color="#C1FF72" fontSize={{ md: "sm", lg: "2xl" }} fontWeight={600}>
                Partnerships
              </Text>
            </Flex>
          }
        />

        <Container maxW="container.xl" mb={{ base: 20, md: 40 }}>
          <Flex gap={8} mt={6} flexDir={{ base: "column", md: "column", lg: "row" }}>
            <Box width="100%" color="#fff">
              <Text fontSize={{ md: "xl", lg: "2xl" }} display="block">
                We offer competitive packages for{" "}
                <chakra.span textDecor="underline">staff</chakra.span> to support students on their
                admission journey.
              </Text>

              <Text display="block" fontSize="2xl" fontWeight={600} mt={6} mb={2}>
                Our Packages:
              </Text>

              <Flex direction="column" gap={8} width="100%">
                <Grid gap={{ base: 4, lg: 4 }} gridTemplateColumns={{ md: "1fr", lg: "140px 1fr" }}>
                  <Box
                    bg="linear-gradient(90deg, rgba(20,227,188,1) 0%, rgba(220,223,17,1) 47%)"
                    p={2}
                    color="#000"
                    fontWeight={600}
                    textAlign="center"
                    fontSize="lg"
                    borderRadius="md"
                    height="44px"
                  >
                    Go Package
                  </Box>

                  <Text>
                    Tailored for Schools who only have between 1-5 students applying to US Colleges.
                  </Text>
                </Grid>

                <Grid gap={{ base: 4, lg: 4 }} gridTemplateColumns={{ md: "1fr", lg: "140px 1fr" }}>
                  <Box width="100%">
                    <Text
                      color="#FFDE5A"
                      textTransform="uppercase"
                      fontSize="xs"
                      mb={1}
                      textAlign={{ base: "center", md: "left" }}
                    >
                      <StarIcon mb={1} /> Most Popular
                    </Text>

                    <Box
                      bg="linear-gradient(90deg, rgba(97,111,249,1) 0%, rgba(248,103,199,1) 47%)"
                      p={2}
                      color="#000"
                      fontWeight={600}
                      textAlign="center"
                      fontSize="lg"
                      borderRadius="md"
                      width="100%"
                    >
                      Pro Package
                    </Box>
                  </Box>

                  <Text>
                    Designed for the schools that have 5-15 students applying to US Colleges.
                    Offering 3 members to use the portal and leverage 150 student submissions.
                  </Text>
                </Grid>

                <Grid gap={{ base: 4, lg: 4 }} gridTemplateColumns={{ md: "1fr", lg: "140px 1fr" }}>
                  <Box
                    bg="linear-gradient(90deg, rgba(231,209,19,1) 0%, rgba(207,166,6,1) 47%)"
                    p={2}
                    color="#000"
                    fontWeight={600}
                    textAlign="center"
                    fontSize="lg"
                    borderRadius="md"
                    height="44px"
                  >
                    Gold Package
                  </Box>

                  <Text>
                    Designed for schools who have +25 students applying to US colleges. This has
                    unlimited college admission reviews, and 300 profiles to view all with unique
                    insights attached to them.
                  </Text>
                </Grid>
              </Flex>
            </Box>

            <Box width="100%">
              <Flex
                direction={{ base: "column", md: "row" }}
                justifyContent="space-between"
                gap={4}
                mt={2}
              >
                <Flex direction="column">
                  <Text
                    fontSize="2xl"
                    fontWeight={600}
                    lineHeight="10px"
                    color="#fff"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    admityogi
                  </Text>

                  <Text
                    color="#EF38AA"
                    fontSize="md"
                    fontWeight={600}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    Profiles
                  </Text>

                  <Text
                    bg="#000"
                    borderRadius="md"
                    color="#fff"
                    padding="1"
                    fontWeight={600}
                    fontSize="14px"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    1 PROFILE = 10 CREDITS
                  </Text>
                </Flex>

                <Flex direction="column">
                  <Text
                    fontSize="2xl"
                    fontWeight={600}
                    lineHeight="10px"
                    color="#fff"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    admityogi
                  </Text>

                  <Text
                    color="#3CB2FD"
                    fontSize="md"
                    fontWeight={600}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    Essays
                  </Text>

                  <Text
                    bg="#000"
                    borderRadius="md"
                    color="#fff"
                    padding="1"
                    fontWeight={600}
                    fontSize="14px"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    1 REVISION = 5 CREDITS
                  </Text>
                </Flex>

                <Flex direction="column">
                  <Text
                    fontSize="2xl"
                    fontWeight={600}
                    lineHeight="10px"
                    color="#fff"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    admityogi
                  </Text>

                  <Text
                    color="#01AB8C"
                    fontSize="md"
                    fontWeight={600}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    ChanceMe
                  </Text>

                  <Text
                    bg="#000"
                    borderRadius="md"
                    color="#fff"
                    padding="1"
                    fontWeight={600}
                    fontSize="14px"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    UNLIMITED USE
                  </Text>
                </Flex>
              </Flex>

              <Box bg="#fff" mt={10} p={4} px={{ md: 0, lg: 2 }} borderRadius="3xl" width="100%">
                <Text
                  fontWeight={600}
                  fontSize={{ base: "lg", md: "2xl" }}
                  ml={{ base: 0, md: 6 }}
                  textAlign={{ base: "center", md: "left" }}
                >
                  Enterprise Subscription <chakra.span fontWeight={400}>(max. Annual)</chakra.span>
                </Text>

                <TableContainer mt={6}>
                  <Table size={{ base: "sm", md: "md", lg: "md" }}>
                    <Thead>
                      <Tr>
                        <Th></Th>
                        <Th color="#000" textTransform="initial" fontSize="lg">
                          Go Package
                        </Th>
                        <Th px={3} py={4} color="#000" textTransform="initial" fontSize="lg">
                          Pro Package
                        </Th>
                        <Th px={3} py={4} color="#000" textTransform="initial" fontSize="lg">
                          Gold Package
                        </Th>
                      </Tr>
                    </Thead>

                    <Tbody>
                      <Tr>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                          pl={"2 !important"}
                          fontWeight={600}
                          p={{ base: "0 !important", md: "3 !important" }}
                        >
                          Profiles
                        </Td>
                        <Td py={4} fontSize={{ base: "12px !important", md: "md !important" }}>
                          50
                        </Td>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                        >
                          150
                        </Td>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                        >
                          300
                        </Td>
                      </Tr>
                      <Tr>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                          pl={"2 !important"}
                          fontWeight={600}
                          p={{ base: "0 !important", md: "3 !important" }}
                        >
                          Essays
                        </Td>
                        <Td py={4} fontSize={{ base: "12px !important", md: "md !important" }}>
                          50
                        </Td>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                        >
                          150
                        </Td>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                        >
                          Unlimited
                        </Td>
                      </Tr>

                      <Tr>
                        <Td px={3} py={4} pl={"2 !important"} fontWeight={600} fontSize="lg">
                          Accounts
                        </Td>
                        <Td py={4}>1</Td>
                        <Td px={3} py={4}>
                          3
                        </Td>
                        <Td px={3} py={4}>
                          5
                        </Td>
                      </Tr>

                      <Tr>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                          pl={"2 !important"}
                          fontWeight={600}
                          p={{ base: "0 !important", md: "3 !important" }}
                        >
                          Price
                        </Td>
                        <Td
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                          fontWeight={600}
                        >
                          $499
                        </Td>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                          fontWeight={600}
                        >
                          $1,099
                        </Td>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                          fontWeight={600}
                        >
                          $2,499
                        </Td>
                      </Tr>
                      <Tr>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                          pl={"2 !important"}
                          fontWeight={600}
                          p={{ base: "0 !important", md: "3 !important" }}
                        >
                          Savings %
                        </Td>
                        <Td
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                          color="#01AB8C"
                          fontWeight={600}
                        >
                          17%
                        </Td>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                          color="#01AB8C"
                          fontWeight={600}
                        >
                          39%
                        </Td>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                          color="#01AB8C"
                          fontWeight={600}
                        >
                          41%
                        </Td>
                      </Tr>
                      <Tr>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                          pl={"2 !important"}
                          fontWeight={600}
                          p={{ base: "0 !important", md: "3 !important" }}
                        >
                          Retail Price
                        </Td>
                        <Td py={4} fontSize={{ base: "12px !important", md: "md !important" }}>
                          $600
                        </Td>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                        >
                          $1,800
                        </Td>
                        <Td
                          px={3}
                          py={4}
                          fontSize={{ base: "12px !important", md: "md !important" }}
                        >
                          $4,200
                        </Td>
                      </Tr>
                      <Tr>
                        <Td px={3} py={4}></Td>

                        <Td py={4}>
                          <Button
                            onClick={() => initPurchase(ENTERPRISE_GO.sku)}
                            isLoading={loading}
                            _hover={{
                              bg: "linear-gradient(90deg, rgba(20,227,188,1) 0%, rgba(220,223,17,1) 47%)",
                              color: "#fff",
                            }}
                            bg="linear-gradient(90deg, rgba(20,227,188,1) 0%, rgba(220,223,17,1) 47%)"
                            size={{ base: "xs", md: "sm" }}
                          >
                            Purchase Go
                          </Button>
                        </Td>

                        <Td px={3} py={4}>
                          <Button
                            onClick={() => initPurchase(ENTERPRISE_PRO.sku)}
                            isLoading={loading}
                            _hover={{
                              bg: "linear-gradient(90deg, rgba(97,111,249,1) 0%, rgba(248,103,199,1) 47%)",
                              color: "#fff",
                            }}
                            bg="linear-gradient(90deg, rgba(97,111,249,1) 0%, rgba(248,103,199,1) 47%)"
                            size={{ base: "xs", md: "sm" }}
                          >
                            Purchase Pro
                          </Button>
                        </Td>

                        <Td px={3} py={4}>
                          <Button
                            onClick={() => initPurchase(ENTERPRISE_GOLD.sku)}
                            isLoading={loading}
                            _hover={{
                              bg: "linear-gradient(90deg, rgba(231,209,19,1) 0%, rgba(207,166,6,1) 47%)",
                              color: "#fff",
                            }}
                            bg="linear-gradient(90deg, rgba(231,209,19,1) 0%, rgba(207,166,6,1) 47%)"
                            size={{ base: "xs", md: "sm" }}
                          >
                            Purchase Gold
                          </Button>
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </TableContainer>

                <Text mt={6} mb={2} textAlign="right" color="#8E43A5" fontSize="sm">
                  PRICE PER PACKAGE
                </Text>

                <Text my={2} ml={2} fontSize="sm" textAlign="center">
                  If you have any questions or concerns, please feel free to contact us{" "}
                  <chakra.a href="mailto:admityogi@gmail.com" textDecor="underline">
                    admityogi@gmail.com
                  </chakra.a>
                </Text>
              </Box>
            </Box>
          </Flex>
        </Container>

        <Container maxW="container.xl" mb={{ base: 20, md: 40 }}>
          <Flex gap={10} mt={6} flexDir={{ base: "column", md: "row" }}>
            <Box width="100%" color="#fff">
              <Text fontSize={{ md: "xl", lg: "2xl" }} display="block">
                AdmitYogi is a new-generation college application platform that combines real
                student data with AI for personalized college guidance.
              </Text>

              <Flex direction="column" gap={20} width="100%" mt={14}>
                <Grid gap={{ base: 4, lg: 6 }} gridTemplateColumns={{ md: "1fr", lg: "60px 1fr" }}>
                  <SiTrustpilot size="60px" />

                  <Text fontSize="2xl">
                    Platform is trusted by over{" "}
                    <chakra.span color="#C1FF72">65,000 families</chakra.span>
                  </Text>
                </Grid>

                <Grid gap={{ base: 4, lg: 6 }} gridTemplateColumns={{ md: "1fr", lg: "60px 1fr" }}>
                  <IoMdSchool size="60px" />

                  <Text fontSize="2xl">
                    Platform aggregates profiles from{" "}
                    <chakra.span color="#C1FF72">50+ US Schools</chakra.span>
                  </Text>
                </Grid>

                <Grid gap={{ base: 4, lg: 6 }} gridTemplateColumns={{ md: "1fr", lg: "60px 1fr" }}>
                  <BiSolidSchool size="60px" />

                  <Text fontSize="2xl">
                    100+ users are{" "}
                    <chakra.span color="#C1FF72">educators or counselors</chakra.span>
                  </Text>
                </Grid>
              </Flex>
            </Box>

            <Box width="100%">
              <Flex direction="column" justifyContent="space-between" gap={6}>
                <Flex
                  bg="#fff"
                  p={6}
                  px={{ md: 4, lg: 6 }}
                  borderRadius="xl"
                  width="100%"
                  gap={12}
                  flexDir={{ base: "column", md: "row" }}
                >
                  <Box
                    width={{ base: "100%", md: "40%" }}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    <Flex direction="column">
                      <Text
                        fontSize="2xl"
                        fontWeight={600}
                        lineHeight="10px"
                        color="#000"
                        textAlign={{ base: "center", md: "left" }}
                      >
                        admityogi
                      </Text>

                      <Text
                        color="#EF38AA"
                        fontSize="md"
                        fontWeight={600}
                        textAlign={{ base: "center", md: "left" }}
                      >
                        Profiles
                      </Text>
                    </Flex>

                    <Text mt={2}>
                      Search successful admits of top schools and read their full application
                      package
                    </Text>

                    <Flex
                      gap={4}
                      mt={12}
                      fontWeight="bold"
                      lineHeight="16px"
                      justify={{ base: "center", md: "left" }}
                    >
                      <Text>
                        <chakra.span color="#EF38AA">6,000+</chakra.span> applications
                      </Text>

                      <Text>
                        <chakra.span color="#EF38AA">10,000+</chakra.span> essays
                      </Text>
                    </Flex>
                  </Box>

                  <Box textAlign={{ base: "center", md: "left" }}>
                    <Text fontWeight={600} textDecoration="underline">
                      What is included?
                    </Text>

                    <chakra.ul mt={4} fontSize="14px">
                      <li>Personal Statements</li>
                      <li>All supplemental essays</li>
                      <li>Extracurricular activities</li>
                      <li>SAT, ACT</li>
                      <li>AP/IB Test Scores</li>
                      <li>Scholarships</li>
                      <li>Q&A and additional info</li>
                    </chakra.ul>
                  </Box>
                </Flex>

                <Flex
                  bg="#fff"
                  p={6}
                  px={{ md: 4, lg: 6 }}
                  borderRadius="xl"
                  width="100%"
                  gap={12}
                  flexDir={{ base: "column", md: "row" }}
                >
                  <Box
                    width={{ base: "100%", md: "40%" }}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    <Flex direction="column">
                      <Text
                        fontSize="2xl"
                        fontWeight={600}
                        lineHeight="10px"
                        color="#000"
                        textAlign={{ base: "center", md: "left" }}
                      >
                        admityogi
                      </Text>

                      <Text
                        color="#3CB2FD"
                        fontSize="md"
                        fontWeight={600}
                        textAlign={{ base: "center", md: "left" }}
                      >
                        Essays
                      </Text>
                    </Flex>

                    <Text mt={2}>Improve essays with advice from AI-enabled admissions coach</Text>

                    <Flex
                      gap={4}
                      mt={14}
                      fontWeight="bold"
                      justify={{ base: "center", md: "left" }}
                    >
                      <Text lineHeight="16px">
                        <chakra.span color="#3CB2FD">10,000+</chakra.span> <br />
                        successful <br /> revisions
                      </Text>
                    </Flex>
                  </Box>

                  <Box textAlign={{ base: "center", md: "left" }}>
                    <Text fontWeight={600} textDecoration="underline">
                      What is included?
                    </Text>

                    <chakra.ul mt={4} fontSize="14px">
                      <li>Central Theme Validation</li>
                      <li>Writing Basics Assessment</li>
                      <li>Admissions Rubric Assessment</li>
                      <li>Grammarly integration (TM)</li>
                      <li>Feedback & Criticique</li>
                      <li>Personalized recommendations</li>
                      <li>Impression Analysis</li>
                      <li>Next Steps </li>
                    </chakra.ul>
                  </Box>
                </Flex>

                <Flex
                  bg="#fff"
                  p={6}
                  px={{ md: 4, lg: 6 }}
                  borderRadius="xl"
                  width="100%"
                  gap={12}
                  flexDir={{ base: "column", md: "row" }}
                >
                  <Box
                    textAlign={{ base: "center", md: "left" }}
                    width={{ base: "100%", md: "40%" }}
                  >
                    <Flex direction="column">
                      <Text
                        fontSize="2xl"
                        fontWeight={600}
                        lineHeight="10px"
                        color="#000"
                        textAlign={{ base: "center", md: "left" }}
                      >
                        admityogi
                      </Text>

                      <Text
                        color="#01AB8C"
                        fontSize="md"
                        fontWeight={600}
                        textAlign={{ base: "center", md: "left" }}
                      >
                        ChanceMe
                      </Text>
                    </Flex>

                    <Text mt={2}>
                      Calculate admission chances and get practical advice to strengthen your
                      profile
                    </Text>

                    <Flex
                      gap={4}
                      mt={14}
                      fontWeight="bold"
                      lineHeight="16px"
                      justify={{ base: "center", md: "left" }}
                    >
                      <Text>
                        <chakra.span color="#01AB8C">6,000</chakra.span> data points
                      </Text>

                      <Text>
                        <chakra.span color="#01AB8C">6</chakra.span>-factor prediction
                      </Text>
                    </Flex>
                  </Box>

                  <Box textAlign={{ base: "center", md: "left" }}>
                    <Text fontWeight={600} textDecoration="underline">
                      What is included?
                    </Text>

                    <chakra.ul mt={4} fontSize="14px">
                      <li>
                        Candidacy assessment (Scores, <br /> Class Rank, Activities, Honors)
                      </li>
                      <li>Extracurricular advice</li>
                      <li>Awards advice</li>
                      <li>6-factor analysis</li>
                      <li>Per-school feedback</li>
                      <li>Strengths analysis</li>
                      <li>Areas of improvement analysis</li>
                    </chakra.ul>
                  </Box>
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </Container>

        <Container maxW="container.xl" mb={{ base: 20, md: 40 }}>
          <Flex gap={10} mt={6} flexDir={{ base: "column", md: "row" }}>
            <Box width={{ base: "100%", md: "40%" }} color="#fff">
              <Flex direction="column">
                <Text
                  fontSize="4xl"
                  fontWeight={600}
                  lineHeight="24px"
                  color="#EF38AA"
                  textAlign={{ base: "center", md: "left" }}
                >
                  Profiles
                </Text>

                <Text
                  color="#fff"
                  fontSize="xl"
                  fontWeight={600}
                  textAlign={{ base: "center", md: "left" }}
                >
                  admityogi
                </Text>
              </Flex>

              <Text fontSize={{ md: "lg", lg: "xl" }} mt={6} display="block">
                Search successful admits of top schools and read their full application package
              </Text>

              <Flex
                gap={4}
                mt={10}
                fontWeight="bold"
                lineHeight="16px"
                justify={{ base: "center", md: "left" }}
              >
                <Text fontSize="xl">
                  <chakra.span display="block" lineHeight="24px" color="#EF38AA">
                    6,000+
                  </chakra.span>{" "}
                  applications
                </Text>

                <Text fontSize="xl">
                  <chakra.span display="block" lineHeight="24px" color="#EF38AA">
                    10,000+
                  </chakra.span>{" "}
                  essays
                </Text>
              </Flex>

              <Box textAlign={{ base: "center", md: "left" }} mt={10}>
                <Text fontWeight={600} textDecoration="underline" fontSize="xl">
                  What is included?
                </Text>

                <chakra.ul mt={4} fontSize="lg">
                  <li>Personal Statements</li>
                  <li>All supplemental essays</li>
                  <li>Extracurricular activities</li>
                  <li>SAT, ACT</li>
                  <li>AP/IB Test Scores</li>
                  <li>Scholarships</li>
                  <li>Q&A and additional info</li>
                </chakra.ul>
              </Box>
            </Box>

            <Flex flexWrap="wrap" justify="center" gap={4}>
              <Image
                src="/images/partnerships/profile-1.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/profile-2.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/profile-3.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/profile-4.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/profile-5.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/profile-6.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
            </Flex>
          </Flex>
        </Container>

        <Container maxW="container.xl" mb={{ base: 20, md: 40 }}>
          <Flex gap={10} mt={6} flexDir={{ base: "column", md: "row" }}>
            <Box width={{ base: "100%", md: "40%" }} color="#fff">
              <Flex direction="column">
                <Text
                  fontSize="4xl"
                  fontWeight={600}
                  lineHeight="24px"
                  color="#3CB2FD"
                  textAlign={{ base: "center", md: "left" }}
                >
                  Essays
                </Text>

                <Text
                  color="#fff"
                  fontSize="xl"
                  fontWeight={600}
                  textAlign={{ base: "center", md: "left" }}
                >
                  admityogi
                </Text>
              </Flex>

              <Text fontSize={{ md: "lg", lg: "xl" }} mt={6} display="block">
                Improve essays with advice from AI-enabled admissions coach
              </Text>

              <Flex
                gap={4}
                mt={10}
                fontWeight="bold"
                lineHeight="16px"
                justify={{ base: "center", md: "left" }}
              >
                <Text fontSize="xl">
                  <chakra.span color="#3CB2FD" display="block" lineHeight="24px">
                    26,000+
                  </chakra.span>
                  successful <br /> revisions
                </Text>
              </Flex>

              <Box textAlign={{ base: "center", md: "left" }} mt={10}>
                <Text fontWeight={600} textDecoration="underline" fontSize="xl">
                  What is included?
                </Text>

                <chakra.ul mt={4} fontSize="lg">
                  <li>Central Theme Validation</li>
                  <li>Writing Basics Assessment</li>
                  <li>Admissions Rubric Assessment</li>
                  <li>Grammarly integration (TM)</li>
                  <li>Feedback & Criticique</li>
                  <li>Personalized recommendations</li>
                  <li>Impression Analysis</li>
                  <li>Next Steps </li>
                </chakra.ul>
              </Box>
            </Box>

            <Flex flexWrap="wrap" justify="center" gap={4}>
              <Image
                src="/images/partnerships/essay-1.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/essay-2.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/essay-3.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/essay-4.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/essay-5.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/essay-6.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
            </Flex>
          </Flex>
        </Container>

        <Container maxW="container.xl" mb={{ base: 20, md: 40 }}>
          <Flex gap={10} mt={6} flexDir={{ base: "column", md: "row" }}>
            <Box width={{ base: "100%", md: "40%" }} color="#fff">
              <Flex direction="column">
                <Text
                  fontSize="4xl"
                  fontWeight={600}
                  lineHeight="24px"
                  color="#01AB8C"
                  textAlign={{ base: "center", md: "left" }}
                >
                  ChanceMe
                </Text>

                <Text
                  color="#fff"
                  fontSize="xl"
                  fontWeight={600}
                  textAlign={{ base: "center", md: "left" }}
                >
                  admityogi
                </Text>
              </Flex>

              <Text fontSize={{ md: "lg", lg: "xl" }} mt={6} display="block">
                Calculate admission chances and get practical advice to strengthen your profile
              </Text>

              <Flex
                gap={4}
                mt={10}
                fontWeight="bold"
                lineHeight="16px"
                justify={{ base: "center", md: "left" }}
              >
                <Text fontSize="xl">
                  <chakra.span color="#01AB8C" lineHeight="24px">
                    6,000
                  </chakra.span>{" "}
                  data <br />
                  points
                </Text>
                <Text fontSize="xl">
                  <chakra.span color="#01AB8C" lineHeight="24px">
                    6
                  </chakra.span>
                  -factor <br />
                  prediction
                </Text>
              </Flex>

              <Box textAlign={{ base: "center", md: "left" }} mt={10}>
                <Text fontWeight={600} textDecoration="underline" fontSize="xl">
                  What is included?
                </Text>

                <chakra.ul mt={4} fontSize="lg">
                  <li>
                    Candidacy assessment (Scores, <br /> Class Rank, Activities, Honors)
                  </li>
                  <li>Extracurricular advice</li>
                  <li>Awards advice</li>
                  <li>6-factor analysis</li>
                  <li>Per-school feedback</li>
                  <li>Strengths analysis</li>
                  <li>Areas of improvement analysis</li>
                </chakra.ul>
              </Box>
            </Box>

            <Flex flexWrap="wrap" justify="center" gap={4}>
              <Image
                src="/images/partnerships/chance-me-2.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/chance-me-3.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/chance-me-4.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/chance-me-5.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/chance-me-6.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
              <Image
                src="/images/partnerships/chance-me-7.png"
                width="300"
                height="250"
                style={{ height: "250px !important", borderRadius: "20px" }}
              />
            </Flex>
          </Flex>
        </Container>

        <Container maxW="container.xl" mb={{ base: 20, md: 40 }}>
          <Flex gap={10} mt={6} flexDir={{ base: "column", md: "row" }}>
            <Box width="100%" color="#fff">
              <Text fontSize={{ md: "xl", lg: "2xl" }} display="block">
                We offer competitive school packages to support students on their admission journey.
              </Text>

              <Text display="block" fontSize="2xl" fontWeight={600} mt={6} mb={2}>
                Our Packages:
              </Text>

              <Flex direction="column" gap={8} width="100%">
                <Grid gap={{ base: 4, lg: 6 }} gridTemplateColumns={{ md: "1fr", lg: "150px 1fr" }}>
                  <Box
                    bg="linear-gradient(90deg, rgba(20,227,188,1) 0%, rgba(220,223,17,1) 47%)"
                    p={2}
                    color="#000"
                    fontWeight={600}
                    textAlign="center"
                    fontSize="lg"
                    borderRadius="md"
                    height="44px"
                    pos="relative"
                  >
                    Go Package
                    <chakra.span
                      pos="absolute"
                      right="-6px"
                      bottom="-16px"
                      textTransform="uppercase"
                      fontSize="xs"
                      bg="#000"
                      color="#fff"
                      p={1}
                      py={0.5}
                      borderRadius="5px"
                    >
                      30 credits
                    </chakra.span>
                  </Box>

                  <Text>
                    Tailored for students applying to a single college, ensuring an insightful
                    understanding of the admissions process and refining their core essay.
                  </Text>
                </Grid>

                <Grid gap={{ base: 4, lg: 6 }} gridTemplateColumns={{ md: "1fr", lg: "150px 1fr" }}>
                  <Box width="100%">
                    <Text
                      color="#FFDE5A"
                      textTransform="uppercase"
                      fontSize="xs"
                      mb={1}
                      textAlign={{ base: "center", md: "left" }}
                    >
                      <StarIcon mb={1} /> Most Popular
                    </Text>

                    <Box
                      bg="linear-gradient(90deg, rgba(231,209,19,1) 0%, rgba(207,166,6,1) 47%)"
                      p={2}
                      color="#000"
                      fontWeight={600}
                      textAlign="center"
                      fontSize="lg"
                      borderRadius="md"
                      width="100%"
                      pos="relative"
                    >
                      Pro Package
                      <chakra.span
                        pos="absolute"
                        right="-6px"
                        bottom="-16px"
                        textTransform="uppercase"
                        fontSize="xs"
                        bg="#000"
                        color="#fff"
                        p={1}
                        py={0.5}
                        borderRadius="5px"
                      >
                        150 credits
                      </chakra.span>
                    </Box>
                  </Box>

                  <Text>
                    Designed for those applying to 3-5 colleges, offering comprehensive student
                    profile insights and extensive essay preparation.
                  </Text>
                </Grid>

                <Grid gap={{ base: 4, lg: 6 }} gridTemplateColumns={{ md: "1fr", lg: "150px 1fr" }}>
                  <Box
                    bg="linear-gradient(90deg, rgba(97,111,249,1) 0%, rgba(248,103,199,1) 47%)"
                    p={2}
                    color="#000"
                    fontWeight={600}
                    textAlign="center"
                    fontSize="lg"
                    borderRadius="md"
                    height="44px"
                    pos="relative"
                  >
                    Max Package
                    <chakra.span
                      pos="absolute"
                      right="-6px"
                      bottom="-16px"
                      textTransform="uppercase"
                      fontSize="xs"
                      bg="#000"
                      color="#fff"
                      p={1}
                      py={0.5}
                      borderRadius="5px"
                    >
                      500 credits
                    </chakra.span>
                  </Box>

                  <Text>
                    Designed for those aiming for 10+ colleges or seeking the utmost in-depth
                    understanding of student profiles and rigorous revisions for personal statements
                    and school-specific essays.
                  </Text>
                </Grid>
              </Flex>
            </Box>

            <Box width="100%">
              <Flex
                direction={{ base: "column", md: "row" }}
                justifyContent="space-between"
                gap={4}
                mt={2}
              >
                <Flex direction="column">
                  <Text
                    fontSize="2xl"
                    fontWeight={600}
                    lineHeight="10px"
                    color="#fff"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    admityogi
                  </Text>

                  <Text
                    color="#EF38AA"
                    fontSize="md"
                    fontWeight={600}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    Profiles
                  </Text>

                  <Text
                    bg="#000"
                    borderRadius="md"
                    color="#fff"
                    padding="1"
                    fontWeight={600}
                    fontSize="14px"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    1 PROFILE = 10 CREDITS
                  </Text>
                </Flex>

                <Flex direction="column">
                  <Text
                    fontSize="2xl"
                    fontWeight={600}
                    lineHeight="10px"
                    color="#fff"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    admityogi
                  </Text>

                  <Text
                    color="#3CB2FD"
                    fontSize="md"
                    fontWeight={600}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    Essays
                  </Text>

                  <Text
                    bg="#000"
                    borderRadius="md"
                    color="#fff"
                    padding="1"
                    fontWeight={600}
                    fontSize="14px"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    1 REVISION = 5 CREDITS
                  </Text>
                </Flex>

                <Flex direction="column">
                  <Text
                    fontSize="2xl"
                    fontWeight={600}
                    lineHeight="10px"
                    color="#fff"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    admityogi
                  </Text>

                  <Text
                    color="#01AB8C"
                    fontSize="md"
                    fontWeight={600}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    ChanceMe
                  </Text>

                  <Text
                    bg="#000"
                    borderRadius="md"
                    color="#fff"
                    padding="1"
                    fontWeight={600}
                    fontSize="14px"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    UNLIMITED USE
                  </Text>
                </Flex>
              </Flex>

              <Box bg="#fff" mt={10} p={4} px={{ md: 0, lg: 4 }} borderRadius="3xl" width="100%">
                <Text fontWeight={600} fontSize={{ base: "lg", md: "2xl" }} ml={6}>
                  Partnership Offering:
                </Text>

                <TableContainer mt={6}>
                  <Table size={{ base: "sm", md: "sm", lg: "md" }}>
                    <Thead>
                      <Tr>
                        <Th></Th>
                        <Th></Th>
                        <Th color="#000" textTransform="initial" fontSize="lg" px={"3 !important"}>
                          Go (30 credits)
                        </Th>
                        <Th color="#000" textTransform="initial" fontSize="lg" px={"3 !important"}>
                          Pro (150 credits)
                        </Th>
                        <Th color="#000" textTransform="initial" fontSize="lg" px={"3 !important"}>
                          Max (500 credits)
                        </Th>
                      </Tr>
                    </Thead>

                    <Tbody>
                      <Tr>
                        <Td fontWeight={600} fontSize="lg" px={"3 !important"}>
                          Retail price
                        </Td>
                        <Td px={"3 !important"} fontWeight={600}>
                          %
                        </Td>
                        <Td px={"3 !important"}>$49</Td>
                        <Td px={"3 !important"}>$165</Td>
                        <Td px={"3 !important"}>$295</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight={600} fontSize="lg" px={"3 !important"}>
                          20-50 packages
                        </Td>
                        <Td px={"3 !important"} fontWeight={600}>
                          10%
                        </Td>
                        <Td px={"3 !important"}>$44</Td>
                        <Td px={"3 !important"}>$149</Td>
                        <Td px={"3 !important"}>#266</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight={600} fontSize="lg" px={"3 !important"}>
                          50-100 packages
                        </Td>
                        <Td px={"3 !important"} fontWeight={600}>
                          15%
                        </Td>
                        <Td px={"3 !important"}>$42</Td>
                        <Td px={"3 !important"}>$140</Td>
                        <Td px={"3 !important"}>$251</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight={600} fontSize="lg" px={"3 !important"}>
                          100+ packages
                        </Td>
                        <Td px={"3 !important"} fontWeight={600}>
                          20%
                        </Td>
                        <Td px={"3 !important"}>$39</Td>
                        <Td px={"3 !important"}>$132</Td>
                        <Td px={"3 !important"}>$236</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </TableContainer>

                <Text mt={8} mb={2} textAlign="right" color="#8E43A5" fontSize="sm">
                  PRICE PER PACKAGE
                </Text>

                <Flex mt={4} flexWrap="wrap" justifyContent="space-between">
                  <Flex alignItems="center" gap={2}>
                    <BsLaptop size="40px" />

                    <Text fontWeight={600} fontSize="sm">
                      Onboarding Webinars
                    </Text>
                  </Flex>

                  <Flex alignItems="center" gap={2}>
                    <BiSupport size="40px" />

                    <Text fontWeight={600} fontSize="sm">
                      Tech & Customer Support
                    </Text>
                  </Flex>

                  <Flex alignItems="center" gap={2}>
                    <TbDeviceAnalytics size="40px" />

                    <Text fontWeight={600} fontSize="sm">
                      Student Analytics
                    </Text>
                  </Flex>
                </Flex>
              </Box>
            </Box>
          </Flex>
        </Container>
      </Box>

      <Box marginTop={{ base: -100, md: -170 }}>
        <Footer />
      </Box>

      <ModalEnterpriseCheckoutForm />

      <ModalEnterpriseError
        open={enterpriseErrorVisible}
        onClose={() => setEnterpriseErrorVisible(false)}
      />

      <ModalAuth
        header="To purchase this package, create an account with us."
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        type="enterprise"
      />
    </>
  );
};

export default withUserData(Partnerships);
